/**
 * Theme Management Utilities
 * 
 * Provides utilities for managing light/dark theme switching
 * and applying theme-aware styles throughout the application.
 */

import { LIGHT_THEME, DARK_THEME, type Theme } from './colors';

// ===== THEME DETECTION =====

/**
 * Detect user's preferred color scheme from system settings
 */
const getSystemTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  
  // Handle test environment where matchMedia might not be properly mocked
  const matchMedia = window.matchMedia;
  if (!matchMedia) return 'light';
  
  const mediaQuery = matchMedia('(prefers-color-scheme: dark)');
  if (!mediaQuery) return 'light';
  
  return mediaQuery.matches ? 'dark' : 'light';
};

/**
 * Get stored theme preference from localStorage
 */
const getStoredTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;
  
  const stored = localStorage.getItem('theme');
  return stored === 'dark' || stored === 'light' ? stored : null;
};

/**
 * Get current theme (stored preference or system default)
 */
const getCurrentTheme = (): Theme => {
  return getStoredTheme() || getSystemTheme();
};

// ===== THEME APPLICATION =====

/**
 * Apply theme to document root
 */
export const applyTheme = (theme: Theme): void => {
  if (typeof document === 'undefined') return;
  
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    const colors = theme === 'dark' ? DARK_THEME : LIGHT_THEME;
    metaThemeColor.setAttribute('content', colors.BG_PRIMARY);
  }
};

/**
 * Toggle between light and dark themes
 */
export const toggleTheme = (): Theme => {
  const currentTheme = getCurrentTheme();
  const newTheme = currentTheme === 'light' ? 'dark' : 'light';
  applyTheme(newTheme);
  return newTheme;
};

/**
 * Initialize theme on app startup
 */
export const initializeTheme = (): Theme => {
  const theme = getCurrentTheme();
  applyTheme(theme);
  return theme;
};

// ===== THEME CONTEXT HELPERS =====

/**
 * Create theme context value object
 */
export const createThemeContextValue = (theme: Theme) => ({
  theme,
  colors: theme === 'dark' ? DARK_THEME : LIGHT_THEME,
  isDark: theme === 'dark',
  isLight: theme === 'light',
  toggle: toggleTheme,
  setTheme: applyTheme,
});

export type ThemeContextValue = ReturnType<typeof createThemeContextValue>;

// ===== SYSTEM THEME LISTENER =====

/**
 * Listen for system theme changes
 */
export const createSystemThemeListener = (
  callback: (theme: Theme) => void
): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  
  const matchMedia = window.matchMedia;
  if (!matchMedia) return () => {};
  
  const mediaQuery = matchMedia('(prefers-color-scheme: dark)');
  if (!mediaQuery) return () => {};
  
  const handleChange = (e: MediaQueryListEvent) => {
    // Only update if user hasn't set a manual preference
    if (!getStoredTheme()) {
      const newTheme = e.matches ? 'dark' : 'light';
      applyTheme(newTheme);
      callback(newTheme);
    }
  };
  
  mediaQuery.addEventListener('change', handleChange);
  
  // Return cleanup function
  return () => mediaQuery.removeEventListener('change', handleChange);
};

/**
 * Create theme-aware style object
 */
export const createThemeStyles = (theme: Theme) => {
  const colors = theme === 'dark' ? DARK_THEME : LIGHT_THEME;
  
  return {
    // Common patterns
    card: {
      backgroundColor: colors.BG_SURFACE,
      color: colors.TEXT_PRIMARY,
      border: `1px solid ${colors.BORDER_DEFAULT}`,
    },
    
    button: {
      primary: {
        backgroundColor: colors.ACTION_PRIMARY,
        color: colors.ACTION_PRIMARY_TEXT,
        border: `1px solid ${colors.ACTION_PRIMARY}`,
      },
      
      secondary: {
        backgroundColor: 'transparent',
        color: colors.TEXT_INTERACTIVE,
        border: `1px solid ${colors.BORDER_INTERACTIVE}`,
      },
      
      success: {
        backgroundColor: colors.ACTION_SUCCESS,
        color: colors.ACTION_SUCCESS_TEXT,
        border: `1px solid ${colors.ACTION_SUCCESS}`,
      },
    },
    
    input: {
      backgroundColor: colors.BG_PRIMARY,
      color: colors.TEXT_PRIMARY,
      border: `1px solid ${colors.BORDER_DEFAULT}`,
    },
    
    text: {
      primary: { color: colors.TEXT_PRIMARY },
      secondary: { color: colors.TEXT_SECONDARY },
      interactive: { color: colors.TEXT_INTERACTIVE },
      success: { color: colors.TEXT_SUCCESS },
      danger: { color: colors.TEXT_DANGER },
      warning: { color: colors.TEXT_WARNING },
    },
  };
};

// ===== USAGE EXAMPLES =====
/**
 * Example React Hook:
 * 
 * import { useState, useEffect } from 'react';
 * import { initializeTheme, createSystemThemeListener, createThemeContextValue } from './theme';
 * 
 * export const useTheme = () => {
 *   const [theme, setTheme] = useState<Theme>(() => initializeTheme());
 *   
 *   useEffect(() => {
 *     const cleanup = createSystemThemeListener(setTheme);
 *     return cleanup;
 *   }, []);
 *   
 *   return createThemeContextValue(theme);
 * };
 * 
 * Example Component Usage:
 * 
 * import { createThemeStyles } from './theme';
 * 
 * const MyComponent = ({ theme }: { theme: Theme }) => {
 *   const styles = createThemeStyles(theme);
 *   
 *   return (
 *     <div style={styles.card}>
 *       <button style={styles.button.primary}>
 *         Primary Action
 *       </button>
 *     </div>
 *   );
 * };
 */